import React, { createContext, useContext, useEffect, useMemo, useState } from 'react';

const NetworkContext = createContext({ offline: false, savedAt: null });

/**
 * Whether the API can be reached, and if pages are showing data saved on the
 * device, when it was saved. Fed by the browser's online/offline events and
 * by the API client ('network:status'), which knows when a request actually
 * failed for lack of signal, which navigator.onLine alone doesn't.
 */
export const NetworkProvider = ({ children }) => {
  const [offline, setOffline] = useState(() => typeof navigator !== 'undefined' && navigator.onLine === false);
  const [savedAt, setSavedAt] = useState(null);

  useEffect(() => {
    const goOffline = () => setOffline(true);
    const goOnline = () => {
      // Optimistic; the next API request confirms or corrects it.
      setOffline(false);
      setSavedAt(null);
    };
    const onStatus = (event) => {
      const { offline: isOffline, savedAt: at } = event.detail || {};
      setOffline(Boolean(isOffline));
      if (!isOffline) setSavedAt(null);
      else if (at) setSavedAt((prev) => (prev ? Math.min(prev, at) : at));
    };
    window.addEventListener('offline', goOffline);
    window.addEventListener('online', goOnline);
    window.addEventListener('network:status', onStatus);
    return () => {
      window.removeEventListener('offline', goOffline);
      window.removeEventListener('online', goOnline);
      window.removeEventListener('network:status', onStatus);
    };
  }, []);

  const value = useMemo(() => ({ offline, savedAt }), [offline, savedAt]);
  return <NetworkContext.Provider value={value}>{children}</NetworkContext.Provider>;
};

export const useNetwork = () => useContext(NetworkContext);
